import {Divider} from '@/components/ui/divider/Divider'
import {PageCardWrapper, PageHeading} from '@/components/ui/page-atoms/PageAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {CreateMedicalReportForm} from '@/features/medical-reports/components/create-medical-report-form/CreateMedicalReportForm'
import {CreateMedicalReportSearchParams} from '@/features/medical-reports/types'
import {useGetProject} from '@/features/projects/queries/useGetProject'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {useTranslation} from '@/translations/i18n'
import {Helmet} from 'react-helmet'
import {useMemo} from 'react'
import {useGetPlannedAccesses} from '@/features/planned-accesses/queries/useGetPlannedAccesses'
import {useGetMultipleAccesses} from '@/features/accesses/queries/useGetMultipleAccesses'

export const CreateMedicalReport = () => {
    const {t} = useTranslation()

    // Parse search params from the URL
    const {searchParams} = useParsedSearchParams(CreateMedicalReportSearchParams, ['accessIds'])

    // Extract report type information if available
    const medicalReportType = searchParams.medicalReportTypeId
        ? {
              id: searchParams.medicalReportTypeId,
              keyName: searchParams.medicalReportTypeKeyName,
              name: searchParams.medicalReportTypeName,
              groupKeyName: searchParams.medicalReportTypeGroupKeyName,
              groupName: searchParams.medicalReportTypeGroupName
          }
        : undefined

    const initialDateFromSearchParams = searchParams.date ? new Date(searchParams.date) : undefined

    const project = useGetProject({
        urlParams: {
            projectId: searchParams?.projectId || ''
        },
        params: {
            responseType: 'extended'
        },
        options: {
            enabled: !!searchParams?.projectId
        }
    })

    const {accesses, isLoading: accessesLoading} = useGetMultipleAccesses({
        accessIds: searchParams?.accessIds || [],
        options: {
            enabled: !!searchParams?.accessIds
        }
    })

    const plannedAccesses = useGetPlannedAccesses({
        urlParams: {
            projectId: searchParams?.projectId || ''
        },
        options: {
            enabled: !!searchParams?.projectId
        }
    })

    const isLoading = useMemo(
        () => accessesLoading || project.isLoading || plannedAccesses.isLoading,
        [accessesLoading, project.isLoading, plannedAccesses.isLoading]
    )

    // Ensure accesses is undefined when no accessIds are provided or when loading
    const initialAccesses = useMemo(() => {
        if (!searchParams?.accessIds || searchParams.accessIds.length == 0 || accessesLoading) {
            return undefined
        }
        return accesses
    }, [searchParams?.accessIds, accesses, accessesLoading])

    return (
        <PageCardWrapper>
            <Helmet title={t('medical_records:create_meta_title')} />
            {isLoading ? (
                <>
                    <PageHeading align="center" justify="space-between">
                        <Skeleton width="100%" height={30} />
                    </PageHeading>
                    <Divider direction="horizontal" />
                </>
            ) : (
                <CreateMedicalReportForm
                    initialProject={project?.data || undefined}
                    initialMedicalReportType={medicalReportType}
                    initialAccesses={initialAccesses}
                    initialPlannedAccesses={plannedAccesses.data}
                    initialDate={initialDateFromSearchParams}
                />
            )}
        </PageCardWrapper>
    )
}
